import { useState } from 'react';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import { IParsedImage, IVideoType } from '@components/typescript';
import { TFullImageWithHeading } from '@features/cms/typescript';
import styles from '@features/cms/components/pages/FullImageWithHeading/FullImageWithHeading.styles';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import Button from '@components/Button/Button';
import sanitizeHtml from '@utils/sanitizeHtml';
import scrollToElementById from '@utils/scrollToElementById';
import VideoPlayer from '@components/Video/VideoPlayer';
type PropsType = {
  data: TFullImageWithHeading;
  isFirst?: boolean;
};
const FullImageWithHeading = ({
  data,
  isFirst = false
}: PropsType) => {
  const CONFIG_SANITIZE = {
    allowedTags: ['br']
  };
  const [, setIsModalOpen] = useState(false);
  const {
    imagesWithBlur,
    backgroundImage,
    title,
    description,
    buttonText,
    buttonUrl,
    video,
    thumbnail,
    code
  } = data;
  let imageSources: IParsedImage | null = backgroundImage ? {
    image: backgroundImage
  } : null;
  const shouldShowVideo = video && Boolean(video?.desktop || video?.tablet || video?.mobile);
  const subtitle = data?.subtitle ? data.subtitle : null;
  const variant: 'default' | 'other' = data?.variant === 'other' ? 'other' : 'default';
  const variantContent = data?.variantContent ? data.variantContent : 'center';
  const variantAlignContent = data?.variantAlignContent ? data.variantAlignContent : 'center';
  const contentWidth = data?.contentWidth ? data.contentWidth : 'medium';
  if (imagesWithBlur) {
    imageSources = {
      image: imagesWithBlur.image,
      blur: imagesWithBlur.blur
    };
  }
  const renderTitle = (): JSX.Element => {
    if (variant === 'other') {
      return <Heading h={isFirst ? 1 : 3} display={3} color={theme.rawColors.neutralWhite} pb={16} mobile>
          {title}
        </Heading>;
    }
    return <Heading h={isFirst ? 1 : 2} display={2} color={theme.rawColors.neutralWhite} pb={16} mobile>
        {title}
      </Heading>;
  };
  const openUrl = (url: string) => {
    window.location.href = url;
  };
  const renderSubtitle = (): JSX.Element | null => {
    if (variant === 'other' && subtitle !== '' && subtitle !== null) {
      return <Heading h={4} headingType={4} color={theme.rawColors.neutralWhite} pb={40} mobile>
          {subtitle}
        </Heading>;
    }
    return null;
  };
  const renderButton = (): JSX.Element | null => buttonUrl && buttonUrl.slice(0, 1) === '#' ? <Button variant="outlined" variantColor="white" size="small" letterCase="lowercase" onClick={() => scrollToElementById(buttonUrl.substring(1))} cy={`${code}-button`} data-testid="full-image-with-heading-button">
        {buttonText}
      </Button> : <Button variant="outlined" variantColor="white" size="small" letterCase="lowercase" onClick={() => buttonUrl ? openUrl(buttonUrl) : setIsModalOpen(true)} cy={`${code}-button`} data-testid="full-image-with-heading-button">
        {buttonText}
      </Button>;
  return <section id={code} sx={styles.fullImageWithHeading(variant, variantContent, variantAlignContent)} data-testid={code}>
      <div sx={styles.fullImageContent(variant, variantContent, contentWidth)} data-testid="full-image-with-heading-content">
        {renderTitle()}
        {renderSubtitle()}
        <p sx={styles.textDescription} color={theme.rawColors.neutralWhite}>
          <span dangerouslySetInnerHTML={sanitizeHtml(description, CONFIG_SANITIZE)} />
        </p>
        {!shouldShowVideo && buttonText && renderButton()}
      </div>
      {shouldShowVideo && <div sx={styles.fullImageVideoContent} data-testid="full-image-with-heading-video-content">
          {buttonText && renderButton()}
          <VideoPlayer video={video as IVideoType} playVideo loop poster={thumbnail || undefined} alt={title} />
        </div>}
      {imageSources && <ResponsiveImage image={imageSources.image} blur={imageSources.blur} alt={title} priority={isFirst} />}
    </section>;
};
export default FullImageWithHeading;